// Centralized user-facing error messages.
// Backend codes (left) → premium customer-facing copy (right).
// Changing a message here updates it everywhere in the app.

import { BffErrorCode } from '@/lib/http/bff-error-codes';

export const NETWORK_ERROR_MESSAGE =
  'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
export const GENERIC_ERROR_MESSAGE =
  'Algo deu errado. Tente novamente em instantes.';

// ─── Auth / OTP ──────────────────────────────────────────────────────────────

export const CODE_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.INVALID_CODE]: 'Código incorreto. Confira e tente novamente.',
  [BffErrorCode.EXPIRED]: 'Código expirado. Volte e tente de novo.',
  [BffErrorCode.TOO_MANY_ATTEMPTS]:
    'Muitas tentativas incorretas. Solicite um novo código.',
  [BffErrorCode.RATE_LIMITED]: 'Aguarde antes de solicitar outro código.',
  [BffErrorCode.SERVER_ERROR]:
    'Não foi possível confirmar agora. Tente novamente em instantes.',
};

export const LOGIN_FALLBACK_ERROR = 'Não foi possível entrar.';
export const LOGIN_TRANSPORT_ERROR =
  'Não foi possível entrar. Tente novamente.';
export const CPF_LOOKUP_ERROR =
  'Não foi possível verificar o CPF. Tente novamente.';
export const RESEND_CODE_ERROR =
  'Não foi possível reenviar o código. Tente novamente.';

// ─── Register ────────────────────────────────────────────────────────────────

export const REGISTER_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.EMAIL_ALREADY_EXISTS]:
    'Já existe uma conta com este e-mail. Use outro e-mail ou faça login.',
  [BffErrorCode.CPF_ALREADY_REGISTERED]:
    'Este CPF já possui um cadastro. Faça login.',
  [BffErrorCode.INVALID_INPUT]:
    'Não foi possível criar o cadastro. Confira os dados e tente de novo.',
};

export const REGISTER_FALLBACK_ERROR =
  'Não foi possível criar o cadastro. Tente novamente.';
export const REGISTER_TRANSPORT_ERROR =
  'Não foi possível criar o cadastro. Tente novamente.';
export const CONFIRM_REGISTER_FALLBACK_ERROR =
  'Não foi possível confirmar o cadastro.';
export const CONFIRM_REGISTER_TRANSPORT_ERROR =
  'Não foi possível confirmar o cadastro. Tente novamente.';
export const CONFIRM_REGISTER_LOGIN_FAILED =
  'Cadastro confirmado, mas não foi possível iniciar o login. Tente novamente.';

// ─── Onboarding ──────────────────────────────────────────────────────────────

export const ADDRESS_SAVE_ERROR = "Não foi possível salvar o endereço. Tente novamente.";
export const COMPLETE_ERROR = "Não foi possível concluir o cadastro. Tente novamente.";
export const TERMS_ACCEPT_ERROR = "Não foi possível registrar o aceite dos termos. Tente novamente.";

// ─── Create event ────────────────────────────────────────────────────────────

export const CREATE_LIST_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.ALREADY_HAS_ACTIVE_LIST]: 'Você já tem uma lista ativa.',
  [BffErrorCode.INVALID_INPUT]:
    'Não foi possível publicar sua lista. Confira os dados e tente de novo.',
};

export const CREATE_LIST_FALLBACK_ERROR =
  'Não foi possível publicar sua lista. Tente novamente.';
export const CREATE_LIST_TRANSPORT_ERROR =
  'Não foi possível publicar sua lista. Tente novamente.';

// ─── Edit list ───────────────────────────────────────────────────────────────

export const EDIT_LIST_ERROR =
  'Não foi possível salvar as alterações. Tente novamente.';

// ─── Host panel ──────────────────────────────────────────────────────────────

/** The PUT to the presigned URL failed (expired, refused type, network): transport, not validation. */
export const COVER_UPLOAD_ERROR = "Não foi possível enviar a foto. Tente novamente.";

// ─── Internal auth ───────────────────────────────────────────────────────────

export const CREDENTIALS_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.INVALID_CREDENTIALS]: 'E-mail ou senha inválidos.',
  [BffErrorCode.UNSUPPORTED_ROLE]: 'Seu perfil não tem acesso ao painel.',
  [BffErrorCode.INVALID_INPUT]: 'Confira o e-mail e a senha informados.',
  [BffErrorCode.TOO_MANY_ATTEMPTS]:
    'Muitas tentativas sem sucesso. Aguarde alguns minutos e tente de novo.',
  [BffErrorCode.SERVER_ERROR]:
    'Não foi possível entrar agora. Tente novamente em instantes.',
};

export const CREDENTIALS_TRANSPORT_ERROR =
  'Não foi possível entrar. Tente novamente.';

/** Notice shown on the login step after a successful password reset. */
export const INTERNAL_PASSWORD_RESET_NOTICE =
  'Senha redefinida. Entre com a senha nova.';

// ─── Internal recovery · request step ────────────────────────────────────────

/** Lede of the "recuperar" step: ask for the corporate e-mail to get a code. */
export const RECOVERY_REQUEST_LEDE =
  'Informe o e-mail corporativo e você recebe um código para definir uma senha nova.';
/** Submit button of the "recuperar" step. */
export const RECOVERY_REQUEST_SUBMIT_LABEL = 'Enviar o código';

/** Backend/BFF codes surfaced while requesting a recovery code → user-facing copy. */
export const RECOVERY_REQUEST_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.SERVER_ERROR]:
    'Não foi possível enviar o código agora. Tente novamente em instantes.',
};

/** Transport failure (network) while requesting a recovery code. */
export const RECOVERY_REQUEST_TRANSPORT_ERROR =
  'Não foi possível enviar o código. Tente novamente.';

// ─── Internal recovery · confirm step ("enviado") ────────────────────────────

/** Lede of the "enviado" step; `{e-mail}` is replaced with the submitted address. */
export function recoverySentLede(email: string): string {
  return `Se ${email} for uma conta ativa, o código de nova senha chega em instantes. Não encontrou? Procure em spam ou fale com o seu gerente.`;
}

/** Code field of the "enviado" step. */
export const RECOVERY_CODE_FIELD_LABEL = 'Código';
export const RECOVERY_CODE_FIELD_PLACEHOLDER = 'Os seis dígitos do e-mail';
/** New-password field of the "enviado" step. */
export const RECOVERY_NEW_PASSWORD_FIELD_LABEL = 'Senha nova';
export const RECOVERY_NEW_PASSWORD_FIELD_PLACEHOLDER = 'Ao menos oito caracteres';

/** Shown when the new-password field is submitted empty. */
export const RECOVERY_NEW_PASSWORD_EMPTY_ERROR = 'Informe a senha nova.';
/** Submit button of the "enviado" step. */
export const RECOVERY_CONFIRM_SUBMIT_LABEL = 'Definir a senha nova';

/** Resend action label of the "enviado" step. */
export const RECOVERY_RESEND_LABEL = 'Reenviar o código';
/** Resend countdown; `{N}` is replaced with the remaining seconds. */
export function recoveryResendCountdown(seconds: number): string {
  return `Você pode pedir outro código em ${seconds} segundos.`;
}
/** Notice shown after a successful resend. */
export const RECOVERY_RESEND_NOTICE =
  'Se o e-mail for de uma conta ativa, um novo código chega em instantes.';

/** Backend/BFF codes surfaced while confirming the new password → user-facing copy. */
export const RECOVERY_CONFIRM_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.INVALID_CODE]:
    'Código incorreto ou expirado. Confira o código ou peça um novo.',
  [BffErrorCode.WEAK_PASSWORD]:
    'A senha nova não atende aos requisitos de segurança. Escolha outra.',
  [BffErrorCode.SERVER_ERROR]:
    'Não foi possível redefinir a senha agora. Tente novamente em instantes.',
};

/** Transport failure (network) while confirming the new password. */
export const RECOVERY_CONFIRM_TRANSPORT_ERROR =
  'Não foi possível redefinir a senha. Tente novamente.';

// ─── Internal auth · shared accessible names ─────────────────────────────────

/** Accessible names for the show/hide password toggle. */
export const PASSWORD_TOGGLE_SHOW_LABEL = 'mostrar senha';
export const PASSWORD_TOGGLE_HIDE_LABEL = 'ocultar senha';
/** Accessible suffix appended to links that open in a new tab. */
export const EXTERNAL_LINK_NEW_TAB_SUFFIX = '(abre em nova aba)';

// ─── Public gift-list search ────────────────────────────────────────────────

export const PUBLIC_SEARCH_INVALID_INPUT_MESSAGE =
  'Confira os critérios da busca e tente novamente.';
export const PUBLIC_SEARCH_FORBIDDEN_MESSAGE =
  'Não foi possível realizar a busca a partir desta origem.';
export const PUBLIC_SEARCH_RATE_LIMITED_MESSAGE =
  'Muitas buscas em pouco tempo. Aguarde e tente novamente.';
export const PUBLIC_SEARCH_SERVER_ERROR_MESSAGE =
  'Não foi possível buscar listas agora. Tente novamente em instantes.';

// ─── Cart / Sacola ────────────────────────────────────────────────────────────

/** Backend codes surfaced while operating the cart → customer-facing copy. */
export const CART_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.CART_LIST_CONFLICT]:
    'Sua sacola já tem itens de outra lista. Esvazie-a para adicionar itens desta lista.',
  [BffErrorCode.CART_EMPTY]: 'Sua sacola está vazia.',
  [BffErrorCode.CART_ITEM_UNAVAILABLE]:
    'Este presente não está disponível no momento.',
  /*
   * Says what the list has room for, and not how much it already received: the progress stays on
   * the server (Req 26.6). "Quantidade" and not "cota", because the person is choosing an
   * amount, not spending an allowance.
   */
  [BffErrorCode.CART_ITEM_QTY_EXCEEDED]:
    'Escolha uma quantidade menor para este presente.',
  [BffErrorCode.CART_LOCKED]:
    'Sua sacola está em pagamento e não pode ser editada agora.',
  [BffErrorCode.INVALID_INPUT]:
    'Não foi possível atualizar a sacola. Tente novamente.',
  [BffErrorCode.SERVER_ERROR]:
    'Não foi possível atualizar a sacola agora. Tente novamente em instantes.',
};

/**
 * A bag of the person's own list, emptied on its way in (`GET /api/carts/me`): nobody gifts their
 * own list. The backend's own sentence (`cart.service.ts`), so the refusal is explicit, never silent.
 */
export const OWN_LIST_PURCHASE_MESSAGE = 'Não é possível comprar itens da própria lista.';

/** Catch-all when the cart mutation returns an unmapped `{ ok: false }`. */
export const CART_FALLBACK_ERROR =
  'Não foi possível atualizar a sacola. Tente novamente.';

/** Transport failure (network) while operating the cart. */
export const CART_TRANSPORT_ERROR =
  'Não foi possível atualizar a sacola. Tente novamente.';

// ─── Checkout / Pagamento ─────────────────────────────────────────────────────

/** Backend/BFF codes surfaced while finishing the checkout → customer-facing copy. */
export const CHECKOUT_ERRORS: Partial<Record<string, string>> = {
  [BffErrorCode.PAYMENT_DECLINED]:
    'Pagamento não autorizado. Confira os dados ou tente outra forma de pagamento.',
  [BffErrorCode.RETRY_LIMIT]:
    'Você atingiu o limite de tentativas de pagamento para este pedido.',
  [BffErrorCode.CART_EMPTY]: 'Sua sacola está vazia.',
  [BffErrorCode.INVALID_INPUT]:
    'Não foi possível concluir a compra. Confira os dados e tente de novo.',
  [BffErrorCode.SERVER_ERROR]:
    'Não foi possível concluir a compra agora. Tente novamente em instantes.',
};

/** Catch-all when the checkout/retry mutation returns an unmapped `{ ok: false }`. */
export const CHECKOUT_FALLBACK_ERROR =
  'Não foi possível concluir a compra. Tente novamente.';

/** Transport failure (network) while finishing the checkout. */
export const CHECKOUT_TRANSPORT_ERROR =
  'Não foi possível concluir a compra. Tente novamente.';

/**
 * Shown when a checkout attempt timed out but the order may have actually
 * been created on the backend (Fix 2 — recovery check found a matching
 * AWAITING_PAYMENT card order with no retrievable Pix data). Distinct from
 * CHECKOUT_TRANSPORT_ERROR: this tells the customer their FIRST attempt may
 * have gone through, so clicking again is safe (it will now retry the SAME
 * order instead of failing with "cart not found").
 */
export const CHECKOUT_POSSIBLY_COMPLETED_ERROR =
  'Sua última tentativa pode ter sido registrada. Verifique se recebeu uma confirmação antes de tentar novamente — se preferir, clique em Finalizar de novo, que continuaremos o mesmo pedido.';

/**
 * Shown when the checkout can't be submitted because the buyer's data
 * (name/e-mail/CPF/endereço) isn't available on the client. The LOGIN path
 * fetches this from `GET /api/auth/customer/me` (see use-checkout-wizard §
 * buyer-data resolution), so this is now a rare edge case rather than a
 * systemic gap — it only fires when that call fails, or when the customer
 * genuinely has no address on file.
 */
export const CHECKOUT_MISSING_BUYER_DATA_ERROR =
  'Não foi possível recuperar seus dados de cadastro para concluir a compra. Tente novamente ou entre em contato com o suporte.';
